import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Flex,
  Text,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Skeleton,
  Badge,
  Tooltip
} from '@chakra-ui/react';
import { WiDaySunny, WiRain, WiCloudy, WiSnow, WiThunderstorm, WiFog, WiDayCloudy } from 'react-icons/wi';
import { MdLocationOn } from 'react-icons/md';
import { OPEN_WEATHER_API_KEY } from '../../config/api_keys';

const Weather = ({ city, lat, lng }) => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use API key from config
  const API_KEY = OPEN_WEATHER_API_KEY;

  useEffect(() => {
    const fetchWeather = async () => {
      try {
        setLoading(true);
        let url;
        
        if (city) {
          url = `https://api.openweathermap.org/data/2.5/weather?q=${city}&units=metric&appid=${API_KEY}`;
        } else if (lat && lng) {
          url = `https://api.openweathermap.org/data/2.5/weather?lat=${lat}&lon=${lng}&units=metric&appid=${API_KEY}`;
        } else {
          // Use geolocation if no city or coordinates provided
          navigator.geolocation.getCurrentPosition(
            async (position) => {
              const { latitude, longitude } = position.coords;
              const geoUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${latitude}&lon=${longitude}&units=metric&appid=${API_KEY}`;
              
              try {
                const response = await axios.get(geoUrl);
                setWeather(response.data);
              } catch (err) {
                setError('Error fetching weather data');
                console.error(err);
              } finally {
                setLoading(false);
              }
            },
            (error) => {
              setError('Location access denied');
              setLoading(false);
            }
          );
          return; // Exit early as we're using geolocation
        }
        
        const response = await axios.get(url);
        setWeather(response.data);
        setLoading(false);
      } catch (err) {
        setError('Error fetching weather data');
        console.error(err);
        setLoading(false);
      }
    };

    fetchWeather();
  }, [city, lat, lng, API_KEY]);

  // Function to get the appropriate weather icon
  const getWeatherIcon = (weatherCode) => {
    if (!weatherCode) return WiCloudy;
    
    // Weather condition codes: https://openweathermap.org/weather-conditions
    if (weatherCode >= 200 && weatherCode < 300) return WiThunderstorm;
    if (weatherCode >= 300 && weatherCode < 400) return WiRain;
    if (weatherCode >= 500 && weatherCode < 600) return WiRain;
    if (weatherCode >= 600 && weatherCode < 700) return WiSnow;
    if (weatherCode >= 700 && weatherCode < 800) return WiFog;
    if (weatherCode === 800) return WiDaySunny;
    if (weatherCode > 800) return WiDayCloudy;
    
    return WiCloudy; // Default icon
  };

  if (loading) {
    return (
      <Box 
        p={4} 
        borderRadius="md" 
        boxShadow="sm" 
        bg="white"
        minW="250px"
      >
        <Skeleton height="100px" />
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        p={4} 
        borderRadius="md" 
        boxShadow="sm"
        bg="white"
        minW="250px"
      >
        <Text color="red.500">{error}</Text>
      </Box>
    );
  }

  if (!weather) return null;

  const WeatherIcon = getWeatherIcon(weather.weather[0].id);

  return (
    <Box 
      p={4} 
      borderRadius="md" 
      boxShadow="sm"
      bg="white"
      minW="250px"
      position="relative"
      overflow="hidden"
    >
      {/* Background pattern for visual effect */}
      <Box
        position="absolute"
        top="0"
        right="0"
        width="120px"
        height="120px"
        borderRadius="50%"
        bg="green.50"
        opacity="0.5"
        zIndex="0"
      />
      
      <Flex direction="column" position="relative" zIndex="1">
        <Flex justify="space-between" align="center" mb={2}>
          <Text fontWeight="bold" fontSize="lg">Weather</Text>
          <Badge colorScheme={weather.main.temp > 30 ? "red" : weather.main.temp > 20 ? "yellow" : "blue"}>
            {weather.main.temp > 30 ? "Hot" : weather.main.temp > 20 ? "Warm" : "Cool"}
          </Badge>
        </Flex>
        
        <Flex align="center" mb={1}>
          <Icon as={MdLocationOn} mr={1} color="gray.500" />
          <Text color="gray.500" fontSize="sm">{weather.name}, {weather.sys.country}</Text>
        </Flex>
        
        <Flex align="center" justify="space-between" mt={3}>
          <Stat>
            <StatNumber>{Math.round(weather.main.temp)}°C</StatNumber>
            <StatLabel>{weather.weather[0].main}</StatLabel>
            <StatHelpText>Feels like {Math.round(weather.main.feels_like)}°C</StatHelpText>
          </Stat>
          
          <Tooltip label={weather.weather[0].description} placement="top">
            <Box>
              <Icon as={WeatherIcon} w={12} h={12} color="green.500" />
            </Box>
          </Tooltip>
        </Flex>
        
        <Flex mt={4} justify="space-between">
          <Box textAlign="center">
            <Text fontSize="xs" color="gray.500">Humidity</Text>
            <Text fontWeight="bold">{weather.main.humidity}%</Text>
          </Box>
          <Box textAlign="center">
            <Text fontSize="xs" color="gray.500">Wind</Text>
            <Text fontWeight="bold">{Math.round(weather.wind.speed * 3.6)} km/h</Text>
          </Box>
          <Box textAlign="center">
            <Text fontSize="xs" color="gray.500">Pressure</Text>
            <Text fontWeight="bold">{weather.main.pressure} hPa</Text>
          </Box>
        </Flex>
      </Flex>
    </Box>
  );
};

export default Weather; 